import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import {
  Brain, MessageSquare, PhoneCall, TrendingUp, CheckCircle2,
  ArrowUpRight, Frown, Smile, Meh, HelpCircle, BarChart3
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const SENTIMENT_COLORS = { positive: '#22c55e', neutral: '#6b7280', negative: '#ef4444' };
const PIE_COLORS = ['#8b5cf6', '#3b82f6', '#06b6d4', '#22c55e', '#eab308', '#ef4444', '#ec4899'];

const ConversationInsights = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchInsights = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calls/insights`, { headers });
      setInsights(res.data);
    } catch {
      toast({ title: 'Failed to load insights', variant: 'destructive' });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchInsights(); }, [fetchInsights]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628] flex items-center justify-center">
      <p className="text-gray-400">Loading insights...</p>
    </div>
  );

  const data = insights || {};
  const questionData = (data.top_questions || []).map(q => ({ name: q.topic, value: q.count }));
  const sentimentData = Object.entries(data.sentiment_breakdown || {}).map(([k, v]) => ({ name: k, value: v }));
  const intentData = Object.entries(data.intent_breakdown || {}).map(([k, v]) => ({ name: k, value: v }));
  const dayData = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => ({
    name: d.slice(0, 3),
    calls: (data.calls_by_day_of_week || {})[d] || 0,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="insights-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Logo linkTo="/dashboard" />
          <div className="h-6 w-px bg-white/10" />
          <h1 className="text-xl font-bold text-white">Conversation Insights</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="insight-metrics">
          {[
            { label: 'Total Conversations', value: data.total_conversations || 0, icon: MessageSquare, color: 'text-purple-400' },
            { label: 'Avg Turns/Call', value: data.avg_turns || 0, icon: PhoneCall, color: 'text-blue-400' },
            { label: 'Resolution Rate', value: `${data.resolution_rate || 0}%`, icon: CheckCircle2, color: 'text-emerald-400' },
            { label: 'AI Handled', value: data.ai_handled || 0, icon: Brain, color: 'text-cyan-400' },
            { label: 'Escalated', value: data.escalated || 0, icon: ArrowUpRight, color: 'text-amber-400' },
            { label: 'Response Quality', value: `${data.avg_response_quality || 0}%`, icon: TrendingUp, color: 'text-pink-400' },
          ].map(m => (
            <div key={m.label} className="bg-white/[0.03] border border-white/10 rounded-xl p-4">
              <m.icon className={`w-5 h-5 ${m.color} mb-2`} />
              <p className="text-2xl font-bold text-white">{m.value}</p>
              <p className="text-[10px] text-gray-500 mt-0.5">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top Questions */}
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-purple-400" /> Top Question Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              {questionData.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No conversation data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={questionData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <XAxis type="number" tick={{ fill: '#6b7280', fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fill: '#d1d5db', fontSize: 11 }} width={140} />
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Sentiment Breakdown */}
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <Smile className="w-4 h-4 text-emerald-400" /> Sentiment Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent>
              {sentimentData.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No sentiment data yet</p>
              ) : (
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="50%" height={220}>
                    <PieChart>
                      <Pie data={sentimentData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4}>
                        {sentimentData.map((entry, i) => <Cell key={i} fill={SENTIMENT_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3">
                    {sentimentData.map((s, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SENTIMENT_COLORS[s.name] || PIE_COLORS[i] }} />
                        <div>
                          <p className="text-white text-sm capitalize">{s.name}</p>
                          <p className="text-gray-500 text-xs">{s.value} calls</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Intent Breakdown */}
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-400" /> Call Intent Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {intentData.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-8">No intent data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={intentData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} paddingAngle={3} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {intentData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                    <Legend wrapperStyle={{ color: '#9ca3af', fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Calls by Day of Week */}
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base flex items-center gap-2">
                <PhoneCall className="w-4 h-4 text-amber-400" /> Calls by Day of Week
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={dayData}>
                  <XAxis dataKey="name" tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }} />
                  <Bar dataKey="calls" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={30} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Top Tags */}
        {(data.top_tags || []).length > 0 && (
          <Card className="bg-gray-900/50 border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-base">Trending Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {data.top_tags.map((t, i) => (
                  <span key={i} className="px-3 py-1.5 bg-purple-500/10 text-purple-400 rounded-lg text-xs border border-purple-500/20">
                    {t.tag} <span className="text-purple-500 ml-1">({t.count})</span>
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default ConversationInsights;
